import React, { useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { InputSwitch } from 'primereact/inputswitch';
import { Button } from 'primereact/button';
import { TabPanel, TabView } from 'primereact/tabview';

const normalizeTheme = (theme) => (typeof theme === 'string' ? theme.trim().toLowerCase() : theme);
const paymentOptionIconPath = (icon) => `${process.env.PUBLIC_URL || ''}/images/payment-options/${icon}`;

const EditForm = ({
	formData,
	typeOptions,
	themeOptions,
	referenceIntakeOptions,
	cupConfigurationOptions,
	languageOptions = [],
	paymentOptionOptions = [],
	loading,
	showSaveButton,
	isThemeMissing,
	isSaveDisabled,
	saveDisabledTooltip,
	machineAccessSettings,
	operatorAccessSettings,
	userAccessSettings,
	operationalScheduleSettings,
	onFieldChange,
	onAllowedLanguagesChange,
	onSubmit,
}) => {
	const [areAllowedLanguagesExpanded, setAreAllowedLanguagesExpanded] = useState(false);
	const [arePaymentOptionsExpanded, setArePaymentOptionsExpanded] = useState(false);
	const preferredLanguageOptions = languageOptions.filter((languageOption) =>
		(formData.allowedLanguageIds || []).includes(languageOption.value),
	);
	const selectedThemeValue =
		themeOptions.find((themeOption) => normalizeTheme(themeOption.value) === normalizeTheme(formData.theme))?.value || '';
	const isThermeTheme = normalizeTheme(formData.theme) === 'therme';
	const handleFieldChange = (fieldName, value) => {
		if (onFieldChange) {
			onFieldChange(fieldName, value);
		}
	};

	const toggleAllowedLanguage = (languageId) => {
		const currentLanguageIds = formData.allowedLanguageIds || [];
		const nextLanguageIds = currentLanguageIds.includes(languageId)
			? currentLanguageIds.filter((currentLanguageId) => currentLanguageId !== languageId)
			: [...currentLanguageIds, languageId];
		onAllowedLanguagesChange(nextLanguageIds);
	};

	const togglePaymentOption = (paymentOptionValue) => {
		const currentPaymentOptions = formData.enabledPaymentOptions || [];
		const nextPaymentOptions = currentPaymentOptions.includes(paymentOptionValue)
			? currentPaymentOptions.filter((currentPaymentOption) => currentPaymentOption !== paymentOptionValue)
			: [...currentPaymentOptions, paymentOptionValue];
		handleFieldChange('enabledPaymentOptions', nextPaymentOptions);
	};

	const handleSubmit = (e) => {
		e.preventDefault();
		if (onSubmit) {
			onSubmit();
		}
	};

	return (
		<div className="surface-0 border-1 surface-border border-round-md overflow-hidden">
			<div className="surface-50 px-3 py-2 border-bottom-1 surface-border">
				<h3 className="text-sm font-semibold text-600 m-0 uppercase">
					<i className="pi pi-cog mr-2"></i>
					Settings
				</h3>
			</div>

			<form onSubmit={handleSubmit} className="p-4">
				<TabView className="machine-detail-settings-tabs" scrollable>
					<TabPanel header="Machine settings" leftIcon="pi pi-cog mr-2">
				{/* Basic Information Section */}
				<div className="mb-5">
					<h4 className="text-xs font-semibold text-500 uppercase mb-4 tracking-wider">Basic Information</h4>
					<div className="grid">
						<div className="col-12 lg:col-6">
							<div className="flex flex-column gap-2">
								<label className="text-sm font-medium text-700">Customer Name</label>
								<InputText
									value={formData.name || ''}
									onChange={(e) => handleFieldChange('name', e.target.value)}
									placeholder="Enter customer name"
									className="w-full"
								/>
							</div>
						</div>

						<div className="col-12 lg:col-6">
							<div className="flex flex-column gap-2">
								<label className="text-sm font-medium text-700">Machine Type</label>
								<Dropdown
									value={formData.type || ''}
									options={typeOptions}
									onChange={(e) => handleFieldChange('type', e.value)}
									placeholder="Select type"
									optionLabel="text"
									optionValue="value"
									className="w-full"
								/>
							</div>
						</div>

						<div className="col-12 lg:col-6">
							<div className="flex flex-column gap-2">
								<label className="text-sm font-medium text-700">UI Theme</label>
								<Dropdown
									value={selectedThemeValue}
									options={themeOptions}
									onChange={(e) => handleFieldChange('theme', e.value)}
									placeholder="Select theme"
									optionLabel="text"
									optionValue="value"
									className={`w-full ${isThemeMissing ? 'p-invalid' : ''}`}
								/>
								{isThemeMissing && (
									<small className="p-error">A theme is required to save this machine.</small>
								)}
							</div>
						</div>

						<div className="col-12 lg:col-6">
							<div className="flex flex-column gap-2">
								<div className="machine-settings-selection-section">
									<button
										type="button"
										className="machine-settings-selection-toggle"
										onClick={() => setAreAllowedLanguagesExpanded((expanded) => !expanded)}
										aria-expanded={areAllowedLanguagesExpanded}
										aria-controls="allowed-languages-list"
										disabled={loading || languageOptions.length === 0}
									>
										<span className="text-sm font-medium text-700">Allowed languages</span>
										<span className="machine-settings-selection-count">
											{(formData.allowedLanguageIds || []).length}/{languageOptions.length}
											<i className={`pi pi-chevron-${areAllowedLanguagesExpanded ? 'up' : 'down'}`} aria-hidden="true" />
										</span>
									</button>
									{areAllowedLanguagesExpanded && (
										<div id="allowed-languages-list" className="machine-settings-selection-list" role="group" aria-label="Allowed languages">
											{languageOptions.map((languageOption) => (
												<label key={languageOption.value} className="machine-settings-selection-option">
													<Checkbox
														inputId={`allowed-language-${languageOption.value}`}
														checked={(formData.allowedLanguageIds || []).includes(languageOption.value)}
														onChange={() => toggleAllowedLanguage(languageOption.value)}
													/>
													<span>{languageOption.text}</span>
												</label>
											))}
										</div>
									)}
								</div>
								<small className="text-xs text-500">Languages users can select on this machine</small>
							</div>
						</div>

						<div className="col-12 lg:col-6">
							<div className="flex flex-column gap-2">
								<label className="text-sm font-medium text-700">Preferred Language</label>
								<Dropdown
									value={formData.preferredLanguageId || ''}
									options={preferredLanguageOptions}
									onChange={(e) => handleFieldChange('preferredLanguageId', e.value)}
									placeholder="Select preferred language"
									optionLabel="text"
									optionValue="value"
									className="w-full"
									disabled={loading || preferredLanguageOptions.length === 0}
								/>
								<small className="text-xs text-500">Language selected by default on this machine</small>
							</div>
						</div>
					</div>
				</div>

				{/* Availability Settings */}
				<div className="mb-5">
					<h4 className="text-xs font-semibold text-500 uppercase mb-4 tracking-wider">Availability</h4>
					<div className="grid">
						<div className="col-12 lg:col-6">
							<div className="flex align-items-center justify-content-between p-3 surface-50 border-round">
								<div className="machine-availability-description">
									<div className="flex align-items-center gap-1">
										<label className="text-sm font-medium text-700">Available for Customer</label>
										<Button
											type="button"
											icon="pi pi-info-circle"
											aria-label="About Available for Customer"
											tooltip="Use this for machines actively operating in the field. Turn it off for temporary or seasonal closures, such as holiday or winter closures in a theme park: the machine is then excluded from KPI calculations and the customer PWA's available-machine list. Mobile Active must also be enabled for PWA display."
											tooltipOptions={{ position: 'top', className: 'available-for-customer-tooltip' }}
											className="p-button-text p-button-rounded p-button-sm p-0"
										/>
									</div>
									<small className="text-xs text-500">
										Include active machines in KPI reporting and make them eligible for the customer PWA.
									</small>
								</div>
								<InputSwitch
									className="machine-availability-switch"
									checked={formData.availableForCustomer || false}
									onChange={(e) => handleFieldChange('availableForCustomer', e.value)}
								/>
							</div>
						</div>

						<div className="col-12 lg:col-6">
							<div className="flex align-items-center justify-content-between p-3 surface-50 border-round">
								<div>
									<label className="text-sm font-medium text-700 block">Mobile Active</label>
									<small className="text-xs text-500">
										{formData.mobileActive ? 'Show QR code on machine interface' : 'Hide QR code on machine interface'}
									</small>
								</div>
								<InputSwitch
									checked={formData.mobileActive || false}
									onChange={(e) => handleFieldChange('mobileActive', e.value)}
								/>
							</div>
						</div>
					</div>
					<div className="grid">
						<div className="col-12 lg:col-12">
							<div className="flex flex-column gap-2">
								<label className="text-sm font-medium text-700">QR code URL</label>
								<InputText
									value={formData.qrCodeUrl || ''}
									onChange={(e) => handleFieldChange('qrCodeUrl', e.target.value)}
									placeholder="https://"
									className="w-full"
								/>
							</div>
						</div>
					</div>
				</div>

				{/* Pricing */}
				<div className="mb-5">
					<h4 className="text-xs font-semibold text-500 uppercase mb-4 tracking-wider">Pricing</h4>
					<div className="grid">
						<div className="col-12 lg:col-4">
							<div className="flex flex-column gap-2">
								<label className="text-sm font-medium text-700">Base Price</label>
								<InputNumber
									value={formData.price || 0}
									onValueChange={(e) => handleFieldChange('price', e.value)}
									mode="currency"
									currency={formData.currency && formData.currency.length === 3 ? formData.currency : 'EUR'}
									locale="nl-NL"
									minFractionDigits={2}
									placeholder="0.00"
									className="w-full"
								/>
							</div>
						</div>

						<div className="col-12 lg:col-4">
							<div className="flex flex-column gap-2">
								<label className="text-sm font-medium text-700">Currency</label>
								<InputText
									value={formData.currency || ''}
									onChange={(e) => handleFieldChange('currency', e.target.value.toUpperCase())}
									placeholder="EUR"
									maxLength={3}
									className="w-full"
								/>
							</div>
						</div>

						<div className="col-12 lg:col-4">
							<div className="flex flex-column gap-2">
								<label className="text-sm font-medium text-700">Soup Offset Price</label>
								<InputNumber
									value={formData.soupOffsetPrice || 0}
									onValueChange={(e) => handleFieldChange('soupOffsetPrice', e.value)}
									mode="currency"
									currency={formData.currency && formData.currency.length === 3 ? formData.currency : 'EUR'}
									locale="nl-NL"
									minFractionDigits={2}
									placeholder="0.00"
									className="w-full"
								/>
							</div>
						</div>

						{!isThermeTheme && (
							<div className="col-12 lg:col-6">
								<div className="flex flex-column gap-2">
									<div className="machine-settings-selection-section">
										<button
											type="button"
											className="machine-settings-selection-toggle"
											onClick={() => setArePaymentOptionsExpanded((expanded) => !expanded)}
											aria-expanded={arePaymentOptionsExpanded}
											aria-controls="payment-options-list"
										>
											<span className="text-sm font-medium text-700">Payment options</span>
											<span className="machine-settings-selection-count">
												{(formData.enabledPaymentOptions || []).length}/{paymentOptionOptions.length}
												<i className={`pi pi-chevron-${arePaymentOptionsExpanded ? 'up' : 'down'}`} aria-hidden="true" />
											</span>
										</button>
										{arePaymentOptionsExpanded && (
											<div id="payment-options-list" className="machine-settings-selection-list" role="group" aria-label="Payment options">
												{paymentOptionOptions.map((paymentOption) => (
													<label key={paymentOption.value} className="machine-settings-selection-option">
														<Checkbox
															inputId={`payment-option-${paymentOption.value}`}
															checked={(formData.enabledPaymentOptions || []).includes(paymentOption.value)}
															onChange={() => togglePaymentOption(paymentOption.value)}
														/>
														<div className="payment-option">
															<img src={paymentOptionIconPath(paymentOption.icon)} alt="" className="payment-option-logo" />
															<span>{paymentOption.label}</span>
														</div>
													</label>
												))}
											</div>
										)}
									</div>
									<small className="text-xs text-500">
										Logos shown during the card-payment flow. This does not change terminal acceptance.
									</small>
								</div>
							</div>
						)}
					</div>
				</div>

				{/* Technical Settings */}
				<div className="mb-5">
					<h4 className="text-xs font-semibold text-500 uppercase mb-4 tracking-wider">Technical Settings</h4>
					<div className="grid">
						<div className="col-12 lg:col-4">
							<div className="flex flex-column gap-2">
								<label className="text-sm font-medium text-700">Steam Deviation</label>
								<InputNumber
									value={formData.steamDeviation || 0}
									onValueChange={(e) => handleFieldChange('steamDeviation', e.value)}
									min={-10000}
									max={10000}
									placeholder="0"
									className="w-full"
									suffix="ms"
								/>
							</div>
						</div>

						<div className="col-12 lg:col-4">
							<div className="flex flex-column gap-2">
								<label className="text-sm font-medium text-700">Blend Deviation</label>
								<InputNumber
									value={formData.blendDeviation || 0}
									onValueChange={(e) => handleFieldChange('blendDeviation', e.value)}
									min={-10000}
									max={10000}
									placeholder="0"
									className="w-full"
									suffix="ms"
								/>
							</div>
						</div>

						<div className="col-12 lg:col-4">
							<div className="flex flex-column gap-2">
								<label className="text-sm font-medium text-700">Cup Configuration</label>
								<Dropdown
									value={formData.cupConfigurationId || ''}
									options={cupConfigurationOptions}
									onChange={(e) => handleFieldChange('cupConfigurationId', e.value)}
									placeholder="Select configuration"
									optionLabel="text"
									optionValue="value"
									filter
									className="w-full"
								/>
							</div>
						</div>

						<div className="col-12 lg:col-6">
							<div className="flex flex-column gap-2">
								<label className="text-sm font-medium text-700">Reference Intake</label>
								<Dropdown
									value={formData.referenceIntake || ''}
									options={referenceIntakeOptions}
									onChange={(e) => handleFieldChange('referenceIntake', e.value)}
									placeholder="Select intake"
									optionLabel="text"
									optionValue="value"
									filter
									className="w-full"
								/>
							</div>
						</div>
					</div>
				</div>

					</TabPanel>
					<TabPanel header="Machine access" leftIcon="pi pi-key mr-2">
						{machineAccessSettings}
					</TabPanel>
					<TabPanel header="Operators" leftIcon="pi pi-users mr-2">
						{operatorAccessSettings}
					</TabPanel>
					<TabPanel header="Users" leftIcon="pi pi-user mr-2">
						{userAccessSettings}
					</TabPanel>
					<TabPanel header="Operational schedule" leftIcon="pi pi-calendar mr-2">
						<div className="mb-5">
							<div className="flex flex-column gap-2">
								<label htmlFor="operational-status-note" className="text-sm font-medium text-700">
									Operational status note
								</label>
								<div className="flex align-items-end gap-2">
									<InputTextarea
										id="operational-status-note"
										value={formData.operationalStatusNote || ''}
										onChange={(e) => handleFieldChange('operationalStatusNote', e.target.value)}
										placeholder="Add a note about the machine's current operational status"
										maxLength={1000}
										rows={3}
										autoResize
										className="flex-1"
									/>
									<Button
										type="button"
										icon="pi pi-times"
										aria-label="Clear operational status note"
										tooltip="Clear operational status note"
										tooltipOptions={{ position: 'top' }}
										className="p-button-text p-button-rounded"
										disabled={!formData.operationalStatusNote}
										onClick={() => handleFieldChange('operationalStatusNote', '')}
									/>
								</div>
								<small className="text-xs text-500">Optional note shown to operators. Maximum 1,000 characters.</small>
							</div>
						</div>
						{operationalScheduleSettings}
					</TabPanel>
				</TabView>

				{/* Action Button - Only show if there are changes */}
				{showSaveButton && (
					<div className="flex justify-content-end pt-4 border-top-1 surface-border">
						<Button
							type="submit"
							label="Save Changes"
							icon="pi pi-check"
							loading={loading}
							disabled={isSaveDisabled}
							tooltip={isSaveDisabled ? saveDisabledTooltip : ''}
							className="px-4 py-2"
						/>
					</div>
				)}
			</form>
		</div>
	);
};

export default EditForm;
